import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { FileText, Upload, Trash2, ExternalLink, Sparkles, Wand2 } from "lucide-react";
import { Badge } from "./ui/badge";

export const CvSection = ({ onProfileParsed }) => {
  const [cv, setCv] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [parsing, setParsing] = useState(false);
  const inputRef = useRef(null);

  const parseProfile = async () => {
    setParsing(true);
    try {
      const { data } = await api.post("/candidate/cv/parse");
      toast.success("Perfil preenchido a partir do CV");
      onProfileParsed?.(data.profile);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setParsing(false); }
  };

  const analyze = async (force = false) => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/candidate/cv/analyze${force ? "?force=true" : ""}`);
      setCv((c) => ({ ...c, analysis: data.analysis }));
      toast.success("Análise concluída");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setAnalyzing(false); }
  };

  useEffect(() => {
    api.get("/candidate/cv").then((r) => setCv(r.data.cv)).catch(() => {});
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("O CV tem de ser um PDF"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Máximo 5 MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/candidate/cv", form);
      setCv(data);
      toast.success("CV carregado — guardado em storage privado");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const view = async () => {
    try {
      const res = await api.get("/candidate/cv/download", { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async () => {
    try {
      await api.delete("/candidate/cv");
      setCv(null);
      toast.success("CV removido");
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div data-testid="cv-section" className="border border-black/5 rounded-xl p-5 bg-slate-50">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Curriculum Vitae (PDF privado)</p>
      <input ref={inputRef} type="file" accept="application/pdf" className="hidden" onChange={upload} data-testid="cv-file-input" />
      {cv ? (
        <>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-amber-500" strokeWidth={1.5} />
            <div>
              <p data-testid="cv-filename" className="text-sm font-semibold text-slate-900">{cv.original_filename}</p>
              <p className="text-xs text-slate-500">{(cv.size / 1024).toFixed(0)} KB · visível apenas para empresas onde te candidatas</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="cv-view-btn" size="sm" variant="outline" className="rounded-full" onClick={view}>
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Ver
            </Button>
            <Button data-testid="cv-replace-btn" size="sm" variant="outline" className="rounded-full" disabled={uploading} onClick={() => inputRef.current?.click()}>
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Substituir
            </Button>
            <Button data-testid="cv-delete-btn" size="sm" variant="ghost" className="rounded-full text-red-600 hover:text-red-700" onClick={remove}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-black/5">
          {cv.analysis ? (
            <div data-testid="cv-analysis" className="space-y-3 text-sm">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" /> Análise IA do teu CV
                <button data-testid="cv-reanalyze-btn" title="Analisar de novo" disabled={analyzing} onClick={() => analyze(true)}
                  className="ml-1 text-slate-400 hover:text-amber-600 transition-colors normal-case tracking-normal font-normal">
                  {analyzing ? "a analisar..." : "↻ refazer"}
                </button>
              </p>
              {cv.analysis.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {cv.analysis.skills.map((s) => <Badge key={s} className="bg-slate-900 text-white hover:bg-slate-900">{s}</Badge>)}
                </div>
              )}
              {cv.analysis.strengths?.length > 0 && (
                <div>
                  <p className="font-semibold text-emerald-700 text-xs mb-1">Pontos fortes</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-0.5">{cv.analysis.strengths.map((s, i) => <li key={`s-${i}-${String(s).slice(0, 30)}`}>{s}</li>)}</ul>
                </div>
              )}
              {cv.analysis.missing_keywords?.length > 0 && (
                <div>
                  <p className="font-semibold text-amber-700 text-xs mb-1">Palavras-chave em falta</p>
                  <p className="text-slate-600">{cv.analysis.missing_keywords.join(", ")}</p>
                </div>
              )}
              {cv.analysis.suggestions?.length > 0 && (
                <div>
                  <p className="font-semibold text-slate-700 text-xs mb-1">Sugestões</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-0.5">{cv.analysis.suggestions.map((s, i) => <li key={`sug-${i}-${String(s).slice(0, 30)}`}>{s}</li>)}</ul>
                </div>
              )}
            </div>
          ) : (
            <Button data-testid="cv-analyze-btn" size="sm" variant="outline" className="rounded-full border-amber-400 text-amber-600 hover:bg-amber-50" disabled={analyzing} onClick={analyze}>
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {analyzing ? "A analisar..." : "Analisar CV com IA"}
            </Button>
          )}
          <Button data-testid="cv-parse-btn" size="sm" variant="outline" className="rounded-full mt-3" disabled={parsing} onClick={parseProfile}>
            <Wand2 className="w-3.5 h-3.5 mr-1.5" /> {parsing ? "A preencher..." : "Preencher perfil com IA"}
          </Button>
        </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-500">Ainda não carregaste o teu CV. As empresas só o veem quando te candidatas.</p>
          <Button data-testid="cv-upload-btn" size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={uploading} onClick={() => inputRef.current?.click()}>
            <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? "A carregar..." : "Carregar CV (PDF)"}
          </Button>
        </div>
      )}
    </div>
  );
};
